import { isTemplateOwnedRow } from "./generatorOwnedColumns";

/**
 * Display buckets for the column-mapping table, top to bottom.
 *
 * Keys first because they define the entity, then what the modeller has actually mapped,
 * then the rest of the source table, and finally the columns the template writes itself —
 * those are noise while modelling, and at 268 source columns they were scattered through
 * the list.
 */
export const COLUMN_ROW_BUCKET = {
  BUSINESS_KEY: 0,
  HASH_KEY: 1,
  MODELLED: 2,
  AVAILABLE: 3,
  TEMPLATE_OWNED: 4
} as const;

export type ColumnRowFacts = {
  entityType: string | null | undefined;
  targetColumnName?: string | null;
  sourceColumnName?: string | null;
  isBusinessKey: boolean;
  isHashKey: boolean;
  isModelled: boolean;
  reservedColumns?: Set<string>;
};

/** Which bucket a row belongs in. Template-owned wins over everything else. */
export function columnRowBucket(facts: ColumnRowFacts): number {
  if (
    isTemplateOwnedRow(
      facts.entityType,
      facts.targetColumnName,
      facts.sourceColumnName,
      facts.reservedColumns
    )
  ) {
    return COLUMN_ROW_BUCKET.TEMPLATE_OWNED;
  }
  if (facts.isBusinessKey) {
    return COLUMN_ROW_BUCKET.BUSINESS_KEY;
  }
  if (facts.isHashKey) {
    return COLUMN_ROW_BUCKET.HASH_KEY;
  }
  return facts.isModelled ? COLUMN_ROW_BUCKET.MODELLED : COLUMN_ROW_BUCKET.AVAILABLE;
}

/**
 * Stable comparator: bucket first, original order within a bucket, so rows never jump
 * around while the modeller is editing them.
 */
export function compareColumnRows(
  left: { bucket: number; index: number },
  right: { bucket: number; index: number }
): number {
  if (left.bucket !== right.bucket) {
    return left.bucket - right.bucket;
  }
  return left.index - right.index;
}
